// components/three/obstacles/ObstacleDebugRenderer.jsx
'use client'

import { useMemo } from 'react'
import * as THREE from 'three'
import { useObstacles } from './ObstacleProvider'

/**
 * Debug visualization for obstacle AABBs.
 * Renders solid semi-transparent boxes with wireframe overlay for all obstacles and no-go zones.
 * 
 * @param {Object} props
 * @param {boolean} [props.enabled=true] - Toggle debug rendering
 * @param {string} [props.obstacleColor='#ff6b35'] - Color for obstacle boxes
 * @param {string} [props.noGoColor='#e63946'] - Color for no-go zone boxes
 * @param {number} [props.opacity=0.3] - Opacity of solid fill (0-1)
 */
export function ObstacleDebugRenderer({ 
  enabled = true, 
  obstacleColor = '#ff6b35',
  noGoColor = '#e63946',
  opacity = 0.3,
}) {
  const { obstacles, noGoZones, isLoaded } = useObstacles()

  const debugMeshes = useMemo(() => {
    if (!isLoaded || !enabled) return null

    const meshes = []

    // Create solid + wireframe boxes for obstacles (respects OBB rotation)
    obstacles.forEach((collider, idx) => {
      const { center, halfExtents, quaternion } = collider
      const position = [center.x, center.y, center.z]
      const quat = [quaternion.x, quaternion.y, quaternion.z, quaternion.w]
      const geo = [halfExtents.x * 2, halfExtents.y * 2, halfExtents.z * 2]

      meshes.push(
        <mesh key={`obstacle-solid-${idx}`} position={position} quaternion={quat}>
          <boxGeometry args={geo} />
          <meshStandardMaterial
            color={obstacleColor}
            transparent
            opacity={opacity}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      )

      meshes.push(
        <mesh key={`obstacle-wire-${idx}`} position={position} quaternion={quat}>
          <boxGeometry args={geo} />
          <meshBasicMaterial
            color={obstacleColor}
            wireframe
            transparent
            opacity={0.9}
          />
        </mesh>
      )
    })

    noGoZones.forEach((collider, idx) => {
      const { center, halfExtents, quaternion } = collider
      const position = [center.x, center.y, center.z]
      const quat = [quaternion.x, quaternion.y, quaternion.z, quaternion.w]
      const displaySize = [halfExtents.x * 2, Math.max(halfExtents.y * 2, 0.5), halfExtents.z * 2]

      meshes.push(
        <mesh key={`nogo-solid-${idx}`} position={position} quaternion={quat}>
          <boxGeometry args={displaySize} />
          <meshStandardMaterial
            color={noGoColor}
            transparent
            opacity={opacity * 0.8}
            side={THREE.DoubleSide}
            depthWrite={false}
          />
        </mesh>
      )

      meshes.push(
        <mesh key={`nogo-wire-${idx}`} position={position} quaternion={quat}>
          <boxGeometry args={displaySize} />
          <meshBasicMaterial
            color={noGoColor}
            wireframe
            transparent
            opacity={0.7}
          />
        </mesh>
      )
    })

    return meshes
  }, [obstacles, noGoZones, isLoaded, enabled, obstacleColor, noGoColor, opacity])

  if (!enabled || !isLoaded) return null

  return <group name="obstacle-debug">{debugMeshes}</group>
}

/**
 * Alternative debug renderer using edges for cleaner visualization.
 * Creates EdgesGeometry for each collider box.
 */
export function ObstacleDebugEdges({ 
  enabled = true, 
  obstacleColor = '#ff6b35',
  noGoColor = '#e63946',
}) {
  const { obstacles, noGoZones, isLoaded } = useObstacles()

  const edgeLines = useMemo(() => {
    if (!isLoaded || !enabled) return null

    const lines = []

    obstacles.forEach((collider, idx) => {
      const { center, size } = collider
      const geo = new THREE.BoxGeometry(size.x, size.y, size.z)
      const edges = new THREE.EdgesGeometry(geo)
      
      lines.push(
        <lineSegments key={`obstacle-edge-${idx}`} position={[center.x, center.y, center.z]}>
          <primitive object={edges} attach="geometry" />
          <lineBasicMaterial color={obstacleColor} linewidth={2} />
        </lineSegments>
      )
    })

    noGoZones.forEach((collider, idx) => {
      const { center, size } = collider
      const displayHeight = Math.max(size.y, 0.3)
      const geo = new THREE.BoxGeometry(size.x, displayHeight, size.z)
      const edges = new THREE.EdgesGeometry(geo)
      
      lines.push(
        <lineSegments key={`nogo-edge-${idx}`} position={[center.x, center.y, center.z]}>
          <primitive object={edges} attach="geometry" />
          <lineBasicMaterial color={noGoColor} linewidth={2} />
        </lineSegments>
      )
    })

    return lines
  }, [obstacles, noGoZones, isLoaded, enabled, obstacleColor, noGoColor])

  if (!enabled || !isLoaded) return null

  return <group name="obstacle-debug-edges">{edgeLines}</group>
}

